import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import React, { lazy, Suspense } from "react";
import { type AdhocPreviewResultQuery } from "../../generated/graphql";
import ExemplarPanel from "./exemplar-panel";

// Lazy so the pdf.js renderer behind the viewer is only downloaded when an
// operator opens an exemplar's source, never on the result panel's first paint.
const AdhocDocumentViewer = lazy(async () => import("./adhoc-document-viewer"));

type Exemplar =
  AdhocPreviewResultQuery["adhocPreviewResult"]["exemplars"][number];

const styles = {
  viewerFallback: { p: 2 },
  fieldValuesDivider: { mb: 1 },
  jsonBox: {
    m: 0,
    p: 1.5,
    bgcolor: "grey.100",
    borderRadius: 1,
    fontFamily: "monospace",
    fontSize: 12,
    overflowX: "auto",
    maxHeight: "70vh",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
} satisfies Record<string, SxProps<Theme>>;

const prettyJson = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/**
 * Ranked list of the exemplars that shaped the extraction (already ordered by
 * the backend; rendered in the given order). Each row drills into the exemplar's
 * source document alongside its corrected field values.
 *
 * The table and drill-in are the shared {@link ExemplarPanel}; this wires the
 * ad-hoc source route and the field-values pane onto it.
 *
 * @param props The component props.
 * @param props.runId The ad-hoc preview run UUID, for the exemplar source route.
 * @param props.exemplars The retrieved exemplars, in backend rank order.
 */
const AdhocExemplarPanel = ({
  runId,
  exemplars,
}: {
  readonly runId: string;
  readonly exemplars: readonly Exemplar[];
}) => (
  <ExemplarPanel
    exemplars={exemplars}
    renderViewer={(exemplar) => (
      <Suspense
        fallback={
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={styles.viewerFallback}
          >
            <CircularProgress size={18} />
            <Typography>Loading viewer…</Typography>
          </Stack>
        }
      >
        <AdhocDocumentViewer
          runId={runId}
          exemplarDocumentId={exemplar.documentId}
        />
      </Suspense>
    )}
    renderDetail={(exemplar) => (
      <>
        <Typography gutterBottom variant="subtitle2">
          Field values
        </Typography>
        <Divider sx={styles.fieldValuesDivider} />
        <Box component="pre" sx={styles.jsonBox}>
          {prettyJson(exemplar.fieldValues)}
        </Box>
      </>
    )}
  />
);

export default AdhocExemplarPanel;
